import React from 'react';

import { Column, TableColumns, TableName } from '@models/column';
import { KeysOfUnion } from '@utils/types';
import {
  BooleanInlineEditor,
  ChipInlineEditor,
  StringInlineEditor,
  NumberInlineEditor,
  ColumnInlineEditor,
  TableInlineEditor,
  TableColumnInlineEditor,
} from '../';
import {
  AggregationDraft,
  FilterDraft,
  JoinDraft,
  JoinConditionDraft,
} from '../../models';

type Drafts = JoinConditionDraft | JoinDraft | FilterDraft | AggregationDraft;

export interface DraftEditorProps<T> {
  tableColumns: TableColumns;
  draft: Partial<T>;
  onDraftChange: (draft: Partial<T>) => void;
}

export function TableColumnEditor<T extends Drafts>(
  props: DraftEditorProps<T> & { label: KeysOfUnion<T> }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof TableColumnInlineEditor
  >[0]['value'];
  return (
    <TableColumnInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
      tableColumns={props.tableColumns}
    />
  );
}

export function ColumnEditor<T extends Drafts>(
  props: DraftEditorProps<T> & {
    label: KeysOfUnion<T>;
    columns: Column[];
  }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof ColumnInlineEditor
  >[0]['value'];
  return (
    <ColumnInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
      columns={props.columns}
    />
  );
}

export function TableEditor<T extends Drafts>(
  props: DraftEditorProps<T> & {
    label: KeysOfUnion<T>;
    tables: TableName[];
  }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof TableInlineEditor
  >[0]['value'];
  return (
    <TableInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
      tables={props.tables}
    />
  );
}

export function NumberEditor<T extends Drafts>(
  props: DraftEditorProps<T> & { label: KeysOfUnion<T> }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof NumberInlineEditor
  >[0]['value'];
  return (
    <NumberInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
    />
  );
}

export function StringEditor<T extends Drafts>(
  props: DraftEditorProps<T> & { label: KeysOfUnion<T> }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof StringInlineEditor
  >[0]['value'];
  return (
    <StringInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
    />
  );
}

export function ChipEditor<T extends Drafts>(
  props: DraftEditorProps<T> & { label: KeysOfUnion<T> }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof ChipInlineEditor
  >[0]['value'];
  return (
    <ChipInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
    />
  );
}

export function BooleanEditor<T extends Drafts>(
  props: DraftEditorProps<T> & { label: KeysOfUnion<T> }
) {
  const label = props.label;
  const value = props.draft[label] as Parameters<
    typeof BooleanInlineEditor
  >[0]['value'];
  return (
    <BooleanInlineEditor
      label={label}
      value={value}
      onValueChange={value =>
        props.onDraftChange({ ...props.draft, [label]: value })
      }
    />
  );
}
